import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixEcomDeliveryV1ShippoServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/shippo-service',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/ecom/v1/shippo-configurations',
        destPath: '/v1/shippo-configurations',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/ecom/v1/shippo-configurations',
        destPath: '/v1/shippo-configurations',
      },
    ],
    _: [
      {
        srcPath: '/ecom/v1/shippo-configurations',
        destPath: '/v1/shippo-configurations',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_ecom_shippo-configurations';

/** Creates a ShippoConfiguration. */
export function createShippoConfiguration(
  payload: object
): RequestOptionsFactory<any> {
  function __createShippoConfiguration({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'shippoConfiguration.createdDate' },
          { path: 'shippoConfiguration.updatedDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'shippoConfiguration.shipFromAddress.geocode.latitude' },
          { path: 'shippoConfiguration.shipFromAddress.geocode.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ecom.v1.shippo_configuration',
      method: 'POST' as any,
      methodFqn: 'wix.ecom.delivery.v1.ShippoService.CreateShippoConfiguration',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomDeliveryV1ShippoServiceUrl({
        protoPath: '/v1/shippo-configurations',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'shippoConfiguration.createdDate' },
              { path: 'shippoConfiguration.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'shippoConfiguration.shipFromAddress.geocode.latitude' },
              { path: 'shippoConfiguration.shipFromAddress.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createShippoConfiguration;
}

/** Retrieves a ShippoConfiguration. */
export function getShippoConfiguration(
  payload: object
): RequestOptionsFactory<any> {
  function __getShippoConfiguration({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.shippo_configuration',
      method: 'GET' as any,
      methodFqn: 'wix.ecom.delivery.v1.ShippoService.GetShippoConfiguration',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomDeliveryV1ShippoServiceUrl({
        protoPath: '/v1/shippo-configurations/{shippoConfigurationId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'shippoConfiguration.createdDate' },
              { path: 'shippoConfiguration.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'shippoConfiguration.shipFromAddress.geocode.latitude' },
              { path: 'shippoConfiguration.shipFromAddress.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getShippoConfiguration;
}

/** Updates a ShippoConfiguration. */
export function updateShippoConfiguration(
  payload: object
): RequestOptionsFactory<any> {
  function __updateShippoConfiguration({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'shippoConfiguration.createdDate' },
          { path: 'shippoConfiguration.updatedDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'shippoConfiguration.shipFromAddress.geocode.latitude' },
          { path: 'shippoConfiguration.shipFromAddress.geocode.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ecom.v1.shippo_configuration',
      method: 'PATCH' as any,
      methodFqn: 'wix.ecom.delivery.v1.ShippoService.UpdateShippoConfiguration',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomDeliveryV1ShippoServiceUrl({
        protoPath: '/v1/shippo-configurations/{shippoConfiguration.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'shippoConfiguration.createdDate' },
              { path: 'shippoConfiguration.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'shippoConfiguration.shipFromAddress.geocode.latitude' },
              { path: 'shippoConfiguration.shipFromAddress.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateShippoConfiguration;
}

/** Deletes a ShippoConfiguration. */
export function deleteShippoConfiguration(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteShippoConfiguration({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.shippo_configuration',
      method: 'DELETE' as any,
      methodFqn: 'wix.ecom.delivery.v1.ShippoService.DeleteShippoConfiguration',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomDeliveryV1ShippoServiceUrl({
        protoPath: '/v1/shippo-configurations/{shippoConfigurationId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteShippoConfiguration;
}

/**
 * Retrieves a list of ShippoConfigurations, given the provided [paging, filtering, and sorting][1].
 *
 * Up to 1,000 ShippoConfigurations can be returned per request.
 *
 * To learn how to query ShippoConfigurations, see [API Query Language][2].
 *
 * [1]: https://dev.wix.com/api/rest/getting-started/sorting-and-paging
 * [2]: https://dev.wix.com/api/rest/getting-started/api-query-language
 */
export function queryShippoConfigurations(
  payload: object
): RequestOptionsFactory<any> {
  function __queryShippoConfigurations({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.shippo_configuration',
      method: 'GET' as any,
      methodFqn: 'wix.ecom.delivery.v1.ShippoService.QueryShippoConfigurations',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomDeliveryV1ShippoServiceUrl({
        protoPath: '/v1/shippo-configurations/query',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'shippoConfigurations.createdDate' },
              { path: 'shippoConfigurations.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'shippoConfigurations.shipFromAddress.geocode.latitude' },
              {
                path: 'shippoConfigurations.shipFromAddress.geocode.longitude',
              },
            ],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveWixEcomDeliveryV1ShippoServiceUrl({
            protoPath: '/v1/shippo-configurations/query',
            data: payload,
            host,
          }),
          data: payload,
        },
      ],
    };

    return metadata;
  }

  return __queryShippoConfigurations;
}

/** Updates extended fields of a ShippoConfiguration without incrementing revision */
export function updateExtendedFields(
  payload: object
): RequestOptionsFactory<any> {
  function __updateExtendedFields({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.shippo_configuration',
      method: 'POST' as any,
      methodFqn: 'wix.ecom.delivery.v1.ShippoService.UpdateExtendedFields',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomDeliveryV1ShippoServiceUrl({
        protoPath: '/v1/shippo-configurations/{id}/update-extended-fields',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'shippoConfiguration.createdDate' },
              { path: 'shippoConfiguration.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'shippoConfiguration.shipFromAddress.geocode.latitude' },
              { path: 'shippoConfiguration.shipFromAddress.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateExtendedFields;
}

/** Adds a delivery region to a Shippo configuration. */
export function addDeliveryRegion(payload: object): RequestOptionsFactory<any> {
  function __addDeliveryRegion({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.shippo_configuration',
      method: 'POST' as any,
      methodFqn: 'wix.ecom.delivery.v1.ShippoService.AddDeliveryRegion',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomDeliveryV1ShippoServiceUrl({
        protoPath:
          '/v1/shippo-configurations/{shippoConfigurationId}/delivery-regions',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'shippoConfiguration.createdDate' },
              { path: 'shippoConfiguration.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'shippoConfiguration.shipFromAddress.geocode.latitude' },
              { path: 'shippoConfiguration.shipFromAddress.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __addDeliveryRegion;
}

/**
 * Removes a delivery region from a Shippo configuration.
 * When passing the ID of the only existing delivery region, removing it will delete the Shippo configuration.
 */
export function removeDeliveryRegion(
  payload: object
): RequestOptionsFactory<any> {
  function __removeDeliveryRegion({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.shippo_configuration',
      method: 'DELETE' as any,
      methodFqn: 'wix.ecom.delivery.v1.ShippoService.RemoveDeliveryRegion',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomDeliveryV1ShippoServiceUrl({
        protoPath:
          '/v1/shippo-configurations/{shippoConfigurationId}/delivery-regions/{deliveryRegionId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'shippoConfiguration.createdDate' },
              { path: 'shippoConfiguration.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'shippoConfiguration.shipFromAddress.geocode.latitude' },
              { path: 'shippoConfiguration.shipFromAddress.geocode.longitude' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __removeDeliveryRegion;
}
